import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import useStore from '../../../../store/store';
import {getDisplayName} from '../helpers/dataHelpers';

export default function useStackedReportSearch({
  tableContainerRef,
  sourcesSorted,
  sourcesConfig = {},
  debounceMs = 200,
  scrollOffset = 180,
} = {}) {
  const setState = useStore(state => state.setState);
  const search = useStore(state => state.search);
  const disableTypingForAccountSelector = useStore(state => state.disableTypingForAccountSelector);
  const prevDisableTypingForAccountSelector = useRef(false);

  const [searchInput, setSearchInput] = useState(search || '');
  const rowEls = useRef(new Map());

  useEffect(() => {
    setSearchInput(search || '');
  }, [search]);

  useEffect(() => {
    const id = setTimeout(() => {
      if ((search || '') !== (searchInput || '')) setState('search', searchInput);
    }, debounceMs);
    return () => clearTimeout(id);
  }, [searchInput, search, debounceMs, setState]);

  const registerRowRef = useCallback((source, el) => {
    if (!source) return;
    if (el) rowEls.current.set(source, el);
    else rowEls.current.delete(source);
  }, []);

  const normalizedQuery = useMemo(() => String(search || '').trim().toLowerCase(), [search]);
  const matchedSources = useMemo(() => {
    if (!normalizedQuery) return [];
    return (
      sourcesSorted?.filter(s => {
        const key = String(s).toLowerCase();
        const label = String(getDisplayName(s, sourcesConfig) || '').toLowerCase();
        return key.startsWith(normalizedQuery) || label.startsWith(normalizedQuery);
      }) || []
    );
  }, [normalizedQuery, sourcesSorted, sourcesConfig]);

  const lastScrolledTo = useRef(null);
  useEffect(() => {
    const first = matchedSources[0];
    if (!first || lastScrolledTo.current === first) return;
    const el = rowEls.current.get(first);
    const sc = tableContainerRef?.current;
    if (!el || !sc) return;
    lastScrolledTo.current = first;
    requestAnimationFrame(() => {
      const scRect = sc.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      sc.scrollTop = Math.max(0, sc.scrollTop + (elRect.top - scRect.top) - scrollOffset);
    });
  }, [matchedSources, tableContainerRef, scrollOffset]);

  const onSearchFocus = useCallback(() => {
    prevDisableTypingForAccountSelector.current = disableTypingForAccountSelector;
    setState('disableTypingForAccountSelector', true);
  }, [disableTypingForAccountSelector, setState]);

  const onSearchBlur = useCallback(() => {
    setState('disableTypingForAccountSelector', prevDisableTypingForAccountSelector.current);
  }, [setState]);

  return {
    search, // debounced global value (used for scroll/highlight)
    searchInput, // immediate input value
    setSearchInput,
    registerRowRef,
    matchedSources,
    onSearchFocus,
    onSearchBlur,
  };
}

